/**
 * Canvas overlay: skeleton + densified torso intermediate points + metrics HUD data.
 * Landmark → pixel mapping respects the same object-fit layout as the <video>.
 */

import type { FrameAnalysis } from "../tracking/pose-engine";
import type { Point } from "../biomechanics/types";
import { LM } from "../biomechanics/landmarks";
import {
  computeVideoLayout,
  landmarkToCanvas,
  type ObjectFitMode,
  type VideoLayout,
} from "./video-layout";

const BONES: Array<[number, number]> = [
  [LM.LEFT_SHOULDER, LM.RIGHT_SHOULDER],
  [LM.LEFT_SHOULDER, LM.LEFT_ELBOW],
  [LM.LEFT_ELBOW, LM.LEFT_WRIST],
  [LM.RIGHT_SHOULDER, LM.RIGHT_ELBOW],
  [LM.RIGHT_ELBOW, LM.RIGHT_WRIST],
  [LM.LEFT_SHOULDER, LM.LEFT_HIP],
  [LM.RIGHT_SHOULDER, LM.RIGHT_HIP],
  [LM.LEFT_HIP, LM.RIGHT_HIP],
  [LM.LEFT_HIP, LM.LEFT_KNEE],
  [LM.LEFT_KNEE, LM.LEFT_ANKLE],
  [LM.RIGHT_HIP, LM.RIGHT_KNEE],
  [LM.RIGHT_KNEE, LM.RIGHT_ANKLE],
];

const VIS_MIN = 0.35;

export interface DrawOptions {
  canvasWidth: number;
  canvasHeight: number;
  /** Intrinsic video frame size (video.videoWidth / videoHeight). */
  videoWidth: number;
  videoHeight: number;
  fit?: ObjectFitMode;
  /** When true, skip video layout and map 0–1 → full canvas (demo poses). */
  identityLayout?: boolean;
  /** Mirror horizontally (front camera selfie view). */
  mirrorX?: boolean;
}

function toPx(p: Point, layout: VideoLayout): { x: number; y: number } {
  return landmarkToCanvas(p.x, p.y, layout);
}

export function drawAnalysis(
  ctx: CanvasRenderingContext2D,
  analysis: FrameAnalysis,
  options: DrawOptions,
): void {
  const {
    canvasWidth: width,
    canvasHeight: height,
    videoWidth,
    videoHeight,
    fit = "contain",
    identityLayout = false,
    mirrorX = false,
  } = options;

  // Do not fill — caller decides demo backdrop vs transparent live video.
  // clearRect is done by the caller before this for live frames.

  const layout = identityLayout
    ? {
        drawWidth: width,
        drawHeight: height,
        offsetX: 0,
        offsetY: 0,
        canvasWidth: width,
        canvasHeight: height,
        videoWidth: width,
        videoHeight: height,
        fit: "contain" as const,
        mirrorX,
      }
    : computeVideoLayout(width, height, videoWidth, videoHeight, fit, mirrorX);

  const { landmarks, torso, metrics } = analysis;

  // Skeleton
  ctx.lineWidth = 3;
  ctx.lineCap = "round";
  ctx.strokeStyle = "rgba(126, 224, 163, 0.75)";
  for (const [a, b] of BONES) {
    const pa = landmarks[a];
    const pb = landmarks[b];
    if (!pa || !pb) continue;
    if ((pa.visibility ?? 1) < VIS_MIN || (pb.visibility ?? 1) < VIS_MIN) continue;
    const A = toPx(pa, layout);
    const B = toPx(pb, layout);
    ctx.beginPath();
    ctx.moveTo(A.x, A.y);
    ctx.lineTo(B.x, B.y);
    ctx.stroke();
  }

  // Joint dots for major landmarks (helps verify alignment)
  const jointIdx = [
    LM.LEFT_SHOULDER,
    LM.RIGHT_SHOULDER,
    LM.LEFT_ELBOW,
    LM.RIGHT_ELBOW,
    LM.LEFT_WRIST,
    LM.RIGHT_WRIST,
    LM.LEFT_HIP,
    LM.RIGHT_HIP,
    LM.LEFT_KNEE,
    LM.RIGHT_KNEE,
    LM.LEFT_ANKLE,
    LM.RIGHT_ANKLE,
  ];
  for (const i of jointIdx) {
    const p = landmarks[i];
    if (!p || (p.visibility ?? 1) < VIS_MIN) continue;
    const c = toPx(p, layout);
    ctx.beginPath();
    ctx.fillStyle = "rgba(126, 224, 163, 0.9)";
    ctx.arc(c.x, c.y, 3.5, 0, Math.PI * 2);
    ctx.fill();
  }

  // Torso side chains (on the body: L shoulder→hip, R shoulder→hip)
  drawChain(ctx, torso.left, layout, "rgba(120, 180, 255, 0.85)", 2.5);
  drawChain(ctx, torso.right, layout, "rgba(120, 180, 255, 0.85)", 2.5);

  // Midline stack — intermediate points BETWEEN shoulders and hips
  drawChain(ctx, torso.midline, layout, "rgba(240, 193, 74, 0.95)", 3.5);

  const intermediates = metrics.intermediateMidline;
  for (const p of intermediates) {
    const c = toPx(p, layout);
    ctx.beginPath();
    ctx.fillStyle = "#f0c14a";
    ctx.arc(c.x, c.y, 5.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = "#0f1419";
    ctx.stroke();
  }

  // Endpoints (shoulder mid / hip mid)
  const ends = [torso.midline[0], torso.midline[torso.midline.length - 1]];
  for (const p of ends) {
    if (!p) continue;
    const c = toPx(p, layout);
    ctx.beginPath();
    ctx.fillStyle = "#7ee0a3";
    ctx.arc(c.x, c.y, 5, 0, Math.PI * 2);
    ctx.fill();
  }

  // Ideal stack chord dashed (same as midline endpoints when curvature=0)
  if (torso.midline.length >= 2) {
    const a = toPx(torso.midline[0], layout);
    const b = toPx(torso.midline[torso.midline.length - 1], layout);
    ctx.setLineDash([6, 6]);
    ctx.strokeStyle = "rgba(255,255,255,0.5)";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
    ctx.setLineDash([]);
  }
}

function drawChain(
  ctx: CanvasRenderingContext2D,
  chain: Point[],
  layout: VideoLayout,
  color: string,
  width: number,
): void {
  if (chain.length < 2) return;
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  const first = toPx(chain[0], layout);
  ctx.moveTo(first.x, first.y);
  for (let i = 1; i < chain.length; i++) {
    const p = toPx(chain[i], layout);
    ctx.lineTo(p.x, p.y);
  }
  ctx.stroke();
}
